import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { Inbox, Loader2, Hash, Truck, Save } from "lucide-react";
import { toast } from "sonner";

export default function Pickups() {
  const nav = useNavigate();
  const [ships, setShips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = () => {
    setLoading(true);
    api.get("/shipments").then((r) => {
      const pickups = r.data.filter((s) => s.is_pickup_request);
      setShips(pickups);
      const e = {};
      pickups.forEach((s) => { e[s.id] = { tracking_number: s.tracking_number || "", assigned_driver_id: s.assigned_driver_id || "" }; });
      setEdits(e);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/users?role=driver").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  const setField = (id, k, v) => setEdits((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const save = async (id) => {
    const e = edits[id];
    if (e.tracking_number && !e.tracking_number.toUpperCase().startsWith("TM")) {
      toast.error("El número de seguimiento debe empezar por TM"); return;
    }
    setSavingId(id);
    try {
      await api.put(`/shipments/${id}`, {
        tracking_number: e.tracking_number || null,
        assigned_driver_id: e.assigned_driver_id || null,
      });
      toast.success("Recogida actualizada");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const isPending = (s) => !s.tracking_number || !s.assigned_driver_id;
  const pending = ships.filter(isPending);
  const shown = ships.filter((s) => {
    if (filter === "pending") return isPending(s);
    if (filter === "assigned") return !isPending(s) && s.status !== "delivered";
    if (filter === "delivered") return s.status === "delivered";
    return true;
  });
  const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-corp";

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader title="Recogidas" subtitle={`${pending.length} pendiente(s) de asignar · ${ships.length} en total`} />

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>
        ) : ships.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No hay solicitudes de recogida.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {[{ v: "pending", l: "Pendientes" }, { v: "assigned", l: "Asignadas" }, { v: "delivered", l: "Entregadas" }, { v: "all", l: "Todas" }].map((f) => (
                <button key={f.v} data-testid={`pickup-filter-${f.v}`} onClick={() => setFilter(f.v)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${filter === f.v ? "bg-corp border-corp text-white" : "border-input text-muted-foreground hover:text-corp hover:border-corp"}`}>
                  {f.l}
                </button>
              ))}
            </div>
            {shown.length === 0 ? (
              <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">No hay recogidas en esta categoría.</div>
            ) : (
            <div className="grid gap-4">
            {shown.map((s) => {
              const needs = !s.tracking_number || !s.assigned_driver_id;
              return (
                <div key={s.id} data-testid={`pickup-${s.id}`} className={`bg-card border p-4 md:p-5 ${needs ? "border-freight" : "border-border"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{s.client_name}</span>
                        <StatusBadge status={s.status} />
                        {needs && <span className="text-[10px] font-bold uppercase bg-freight text-black px-2 py-0.5">Pendiente</span>}
                        {s.tracking_number && <span className="font-mono text-xs font-bold text-corp">{s.tracking_number}</span>}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">{s.description}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{s.origin} → {s.destination} · {s.packages} bulto(s)</div>
                      {s.documents?.length > 0 && <div className="text-xs text-corp font-semibold mt-1">{s.documents.length} documento(s) adjunto(s)</div>}
                    </div>
                    <button onClick={() => nav(`/shipments/${s.id}`)} data-testid={`pickup-detail-${s.id}`} className="text-xs font-bold uppercase text-muted-foreground hover:text-freight transition-colors">Ver detalle</button>
                  </div>
                  <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-4 pt-4 border-t border-border items-end">
                    <div>
                      <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1"><Hash className="w-3 h-3" /> Nº seguimiento (TM)</label>
                      <input data-testid={`pickup-tm-${s.id}`} className={inputCls} placeholder="TM-XXXXXX"
                        value={edits[s.id]?.tracking_number || ""} onChange={(e) => setField(s.id, "tracking_number", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1"><Truck className="w-3 h-3" /> Conductor</label>
                      <select data-testid={`pickup-driver-${s.id}`} className={inputCls}
                        value={edits[s.id]?.assigned_driver_id || ""} onChange={(e) => setField(s.id, "assigned_driver_id", e.target.value)}>
                        <option value="">Sin asignar</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    <button data-testid={`pickup-save-${s.id}`} onClick={() => save(s.id)} disabled={savingId === s.id}
                      className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 h-[38px] flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                      {savingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
